import * as R from 'fp-ts/Record';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { AvailabilityStatus, Player } from '../generated/graphql';

export type PlayerAvailabilities = Record<string, AvailabilityStatus>;

/**
 * Builds a map of player ID to availability status for easy lookup
 * of a given player's availability
 *
 * @param availabilities A list of player-availability objects
 */
export const makeAvailabilityMap = (
  availabilities: {
    status: AvailabilityStatus;
    player: Pick<Player, 'id'>;
  }[],
) => {
  return pipe(
    availabilities,
    A.map((a) => [a.player.id, a.status] as [string, AvailabilityStatus]),
    R.fromEntries,
  );
};

export interface PlayerWithAvailability {
  player: Pick<Player, 'id' | 'name'>;
  availability: AvailabilityStatus;
}

type AvailabilityItem = {
  status: AvailabilityStatus;
  player: Pick<Player, 'id'>;
};

export const getPlayerListWithAvailability = (
  availabilities: AvailabilityItem[],
  players: Pick<Player, 'id' | 'name'>[],
): PlayerWithAvailability[] => {
  const availabilityLookup = makeAvailabilityMap(availabilities);

  return pipe(
    players,
    A.map((player) => ({
      player,
      availability: pipe(
        availabilityLookup,
        R.lookup(player.id),
        O.getOrElse<AvailabilityStatus>(() => 'UNDECLARED'),
      ),
    })),
  );
};
